import axios from '@/config/axios.config';
import vars from '@/config/vars';
import PaymentTypes from '@/models/PaymentTypes';
import mocked from '@/mockedOrderStatus';
import timeHelper from '@/helpers/timeHelper';
import PaymentNotificator from '@/services/PaymentNotificator';
import PaymentStatus from '@/models/PaymentStatus';
import { logEvent, logException } from '@/helpers/cieloLogger';

export default {
    namespaced: true,

    state: {
        OrderId: null,
        OrderNumber: null,
        Status: null,
        MerchantName: null,
        Customer: {
            Name: null,
            Phone: null,
            Identity: null
        },
        ShippingAddress: {
            ZipCode: null,
            Street: null,
            Number: null,
            Complement: null,
            District: null,
            City: null,
            State: null
        },
        ShippingMethod: {
            Description: null,
            Price: null,
            Type: null
        },
        TotalPrice: null,
        PaymentMethod: {
            Type: null,
            Brand: null,
            InstallmentsNumber: null,
            InstallmentPrice: null,
            CardLastFour: null,
            IsRecurringPayment: false
        },
        /**
         * Itens do carrinho
         * @param {number} Item.unitPrice
         * @param {number} Item.quantity
         * @param {string} Item.name
         * @param {string} Item.description
         */
        ShoppingCartItems: [],
        FirstItemDescription: null,
        CartDiscount: {
            DiscountPercentage: null,
            DiscountPrice: null
        },
        CreatedDate: null,
        ShortUrl: null,
        HasOrderStatusData: false,
        IdPayLink: null,
        MerchantIdentity: null,
        EndToEndId: null
    },

    actions: {
        async pollingFetchOrderStatus({commit}, orderId) {
            try {
                let response;

                if (vars.USE_SIMULATED_MODE)
                    response = await new Promise(resolve => setTimeout(() => resolve({data: mocked}), 3000));
                else
                    response = await axios.get(`/OrderStatus/${orderId}`);

                let status = response.data.paymentStatus.toLowerCase();
                
                // 'authorizedidpaypending' é o valor que vem da api antes de passar pelo nosso de/para
                if(status !== 'authorizedidpaypending') {
                    window.location.reload();
                }
            }
            catch (error) {
                throw error;
            }
        },
        async fetchOrderStatus({
            state,
            commit,
            dispatch
        }, orderId) {
            try {
                let response;

                if(!state.orderId || state.orderId !== orderId) {
                    commit('orderStatus/setOrderId', orderId, { root: true });
                }

                if (vars.USE_SIMULATED_MODE)
                    response = await new Promise(resolve => setTimeout(() => resolve({data: mocked}), 3000));
                else
                    response = await axios.get(`/OrderStatus/${orderId}`);

                let data = response.data;

                commit('orderStatus/setStatus', data, { root: true });
                
                if(!state.Status) return dispatch('pipelineV2/pushTo', 'InvalidUrl', { root: true });
                commit('orderStatus/setFromRequest', data, { root: true });
                commit('orderStatus/setHasOrderStatusData', true, { root: true });
            }
            catch (error) {
                throw error;
            }
        },

        mountOrderStatus({
            state,
            commit,
            dispatch,
            rootState,
            rootGetters
        }) {
            const selectedPaymentMethod = rootGetters['payment/getSelectedPaymentMethod'];
            const shippingMethod = rootGetters['shippingType/getMethod'];

            let data = {
                orderNumber: rootState.preorder.OrderNumber,
                merchantName: rootState.preorder.MerchantName,
                customerName: rootState.customer.Name,
                customerPhone: rootState.customer.Phone,
                customerIdentity: rootState.customer.Identity,
                shippingZipCode: rootState.shippingAddress.ZipCode,
                shippingStreet: rootState.shippingAddress.Street,
                shippingNumber: rootState.shippingAddress.Number,
                shippingComplement: rootState.shippingAddress.Complement,
                shippingDistrict: rootState.shippingAddress.District,
                shippingCity: rootState.shippingAddress.City,
                shippingState: rootState.shippingAddress.State,
                paymentType:  rootState.payment.PaymentType,
                cardBrand: selectedPaymentMethod.Image,
                installmentsNumber: rootState.payment.NumberOfInstallments,
                installmentPrice: rootState.payment.TotalPrice/rootState.payment.NumberOfInstallments,
                CardLastFour: rootGetters['cardPayment/getLast4DigitsFromCardNumber'],
                isRecurringPayment: rootGetters['recurrentPayment/isRecurringPayment'],
                shippingMethod: shippingMethod.description,
                shippingPrice: shippingMethod.price,
                shippingType: shippingMethod.id,
                items: rootState.cart.Items,
                discountAmount: rootGetters['discount/getDiscountAmount'],
                createdDate: rootGetters['preorder/getOrderDate'],
                totalAmount: rootState.payment.TotalPrice,
            };

            if(!state.Status || data.cardBrand === 'Undefined') return dispatch('pipelineV2/pushTo', 'InvalidUrl', { root: true });
            commit('orderStatus/setFromRequest', data, { root: true });
        },
        
        async listenToRealTimeNotification({state}) {
            try {
                if (!state.OrderId)
                    return;

                await listenToRealTimeNotification(state.OrderId);
            } catch (error) {
                throw error;
            }
        }
    },

    getters: {
        getOrderId: state => state.OrderId,
        getOrderNumber: state => state.OrderNumber,
        getStatus: state => state.Status,
        getMerchantName: state => state.MerchantName,
        getCustomer: state => state.Customer,
        getShippingAddress: state => state.ShippingAddress,
        getShippingMethod: state => state.ShippingMethod,
        getTotalPrice: state => state.TotalPrice,
        getShoppingCartItems: state => state.ShoppingCartItems,
        getFirstItemDescription: state => state.FirstItemDescription,
        getCartDiscount: state => state.CartDiscount,
        getPaymentMethod: state => state.PaymentMethod,
        getCreatedDate: state => state.CreatedDate,
        getShortUrl: state => state.ShortUrl,
        hasOrderStatusData: state => state.HasOrderStatusData,
        getIdPayLink: state => state.IdPayLink,
        getMerchantIdentity: state => state.MerchantIdentity,
        getEndToEndId: state => state.EndToEndId
    },

    mutations: {
        setHasOrderStatusData(state, value) {
            state.HasOrderStatusData = value;
        },
        setFromRequest(state, data) {
            const { description } = data.items.find((item) => item.description) || {};

            state.OrderNumber = data.orderNumber;

            state.MerchantName = data.merchantName;

            state.Customer = {
                Name: data.customerName,
                Phone: data.customerPhone,
                Identity: data.customerIdentity,
            };

            state.ShippingAddress = {
                ZipCode: data.shippingZipCode || null,
                Street: data.shippingStreet || null,
                Number: data.shippingNumber || null,
                Complement: data.shippingComplement || null,
                District: data.shippingDistrict || null,
                City: data.shippingCity || null,
                State: data.shippingState || null
            };

            state.PaymentMethod = {
                Type: data.paymentType,
                Brand: data.cardBrand.toLowerCase(),
                InstallmentsNumber: data.installmentsNumber,
                InstallmentPrice: data.installmentPrice,
                CardLastFour: data.cardLastFour,
                IsRecurringPayment: data.isRecurringPayment
            };

            state.ShippingMethod = {
                Description: data.shippingMethod,
                Price: data.shippingPrice,
                Type: data.shippingType
            };

            state.ShoppingCartItems = data.items;
            state.FirstItemDescription = description;

            state.CartDiscount = {
                DiscountPrice: data.discountAmount,
                DiscountPercentage: data.discountPercentage,
            };

            state.CreatedDate = data.createdDate;
            state.ShortUrl = data.shortUrl;
            state.TotalPrice = data.totalAmount;

            if(data.idPayLink) {
                state.IdPayLink = data.idPayLink;
            }

            state.MerchantIdentity = data.merchantIdentity;
            state.EndToEndId = data.endToEndId;

            logEvent({
                category: 'checkout',
                action: 'finalizacao_pedido',
                label: `OrderStatus: ${state.Status}`
            });
        },
        setOrderId(state, value) {
            state.OrderId = value;
        },
        setStatus(state, data) {
            let status = data.paymentStatus.toLowerCase();
            
            if(status === 'pending' && data.paymentType === PaymentTypes.PIX_OPEN_FINANCE ) status = 'openfinancepending';

            switch (status) {
            case 'paid':
                state.Status = 'paid';
                break;
            case 'denied':
            case 'notfinalized':
                state.Status = 'denied';
                break;
            case 'pending':
            case 'authorized':
                state.Status = 'pending';
                break;
            case 'authorizedidpaypending':
                state.Status = 'idpay_pending';
                break;
            case 'openfinancepending':
                state.Status = 'openfinancepending';
                break;
            case 'voided':
                state.Status = 'canceled';
                break;
            default:
                state.Status = '';
                break;
            }
        },
    }
};

async function listenToRealTimeNotification(orderId) {
    try {
        if (vars.USE_SIMULATED_MODE)
            window.location.reload();

        PaymentNotificator.OnStatusChange((status) => {
            if (status == PaymentStatus.PAID)
                window.location.reload();

            PaymentNotificator.CloseConnection();
        });
      
        await PaymentNotificator.StartConnection(orderId);

        setTimeout(
            () => PaymentNotificator.CloseConnection(), 
            timeHelper.minutesToMs(vars.MINUTES_TO_CLOSE_HUB_CONNECTION)
        );

    } catch (error) {
        logException(error);
        PaymentNotificator.CloseConnection();
    }
}
